import { anyOf, reads, type GrantSpec } from "../../util/access";
export type StockTraceModalKind = "sale" | "movement" | "supply" | "conversion";

const normalize = (value: string) =>
	value
		.trim()
		.toLowerCase()
		.normalize("NFD")
		.replace(/\p{M}/gu, "");

const matchesOperation = (operation: string, ...keywords: string[]) => {
	const normalized = normalize(operation);
	return keywords.some((keyword) => {
		const key = normalize(keyword);
		return normalized === key || normalized.includes(key);
	});
};

const isExternalDepot = (value?: string) =>
	matchesOperation(
		value || "",
		"external in",
		"external_in",
		"externe",
		"inbound",
		"entree externe",
		"entrée externe",
	);

export const toStockTraceModalBundle = (row: Record<string, unknown>) => ({
	...row,
	batch: (row.batch as string | undefined) ?? (row.reference as string | undefined),
});

export const resolveStockTraceModalKind = (row: {
	operation?: string;
	reference?: string;
	batch?: string;
	destination?: string;
	source?: string;
}): StockTraceModalKind => {
	const operation = row.operation || "";
	const reference = String(row.batch || row.reference || "").trim();
	const referenceUpper = reference.toUpperCase();
	const isReturnBatch = referenceUpper.startsWith("RET:");

	if (
		matchesOperation(
			operation,
			"return supply",
			"retour approvisionnement",
			"retour appro",
			"return receipt",
		)
		|| (matchesOperation(operation, "return", "retour") && isExternalDepot(row.destination))
		|| (isReturnBatch && isExternalDepot(row.destination))
		|| (matchesOperation(operation, "entry", "entrée", "entree") && isExternalDepot(row.destination))
	) {
		return "supply";
	}

	if (
		matchesOperation(operation, "sale", "sales", "vente", "ventes", "facture")
		|| matchesOperation(operation, "return sale", "retour vente")
		|| (matchesOperation(operation, "return", "retour") && !matchesOperation(operation, "supply", "appro"))
	) {
		return "sale";
	}

	if (matchesOperation(operation, "supply", "receipt", "approvisionnement", "réception", "reception")) {
		return "supply";
	}

	if (matchesOperation(operation, "conversion", "kit", "de-kit", "dekit", "swap")) {
		return "conversion";
	}

	if (
		matchesOperation(
			operation,
			"inventory",
			"inventaire",
			"adjustment",
			"ajustement",
			"transfer",
			"movement",
			"move",
			"entry",
			"discard",
			"restore",
			"restauration",
			"rebut",
			"transfert",
			"entrée",
			"entree",
			"sortie",
		)
	) {
		return "movement";
	}

	if (referenceUpper.startsWith("INV")) return "movement";
	if (referenceUpper.startsWith("I")) return "sale";
	if (referenceUpper.startsWith("S")) return "supply";
	if (referenceUpper.startsWith("C")) return "conversion";
	if (referenceUpper.startsWith("M")) return "movement";

	return "movement";
};

/**
 * Read grants that let a traced document be opened, per kind.
 * The trace listing itself is its own grant (`stock_tracing · read`), so opening
 * the document behind a row is where that document's own read grant applies; the
 * backend re-checks it and its data scope.
 */
const MODAL_GRANTS: Record<StockTraceModalKind, GrantSpec> = {
	// /records/<batch> asks for the read grant of the row's own kind; this
	// modal opens transfers, discards and counts.
	movement: anyOf(reads("movement"), reads("discard"), reads("inventory")),
	// A sale and a return are two postings, and this route serves both.
	sale: anyOf(reads("sale"), reads("return")),
	supply: reads("supply"),
	conversion: reads("conversion"),
};

export const stockTraceModalGrant = (kind: StockTraceModalKind): GrantSpec =>
	MODAL_GRANTS[kind] ?? MODAL_GRANTS.movement;

export const hasStockTraceReference = (row: { reference?: string; batch?: string }) =>
	!!String(row.batch || row.reference || "").trim();
